/**
 * The Charter map overlays (M5 plan C17, spec §15.3, seam S-M3-7): five `MapOverlayLayer`s in M4's overlay
 * registry, built from the strategic galaxy view (claim and state summaries, war fronts) and the relations view
 * (the player's wanted levels):
 *
 * - **Claims**: the holder's colour and mark; contested systems get the hatch glyph `▨` and are flagged `hatched`;
 *   unclaimed systems are hollow (small, grey, `○`).
 * - **States**: a glyph per active state (and the stage word in the tooltip).
 * - **Wars and fronts**: relays between systems whose holders are at war drawn red, wide and dashed; systems at
 *   war marked `⚔`.
 * - **Law**: your wanted level in each system's jurisdiction (`W1`..`W3`, or `∅` for an unclaimed system: "UNCLAIMED · NO CLAIM LAW", CL-7). A corporation claim reads
 *   "‹Corp› claim" in the corporation's colour with its `★` mark, never lawless (D-424 D5, spec §27.7).
 * - **Tolls**: node size grows with the toll, badged with the toll and the tariff.
 *
 * Colour-blind safe (style guide §9.5): every layer carries a glyph or a size, never hue alone. The layer styles
 * are pure (`charterNodeStyle`, `charterEdgeStyle`), tested on crafted views. Fetching: the galaxy view is followed
 * only while a map is open (`onMapOpenChange`), as M4's economy overlays do; the corporation's name and livery are
 * read once per map open (`corpView`). UI events only.
 */
import {
  notifyMapOverlays,
  onMapOpenChange,
  registerMapOverlay,
  type MapOverlayEdgeStyle,
  type MapOverlayLayer,
  type MapOverlayNodeStyle,
} from '../../maps/model/overlays'
import { logger } from '../../core/logger'
import { CORP_CLAIM_LABELS, UNCLAIMED_LABELS, fillLabel } from '../../features/charterHud/hudModel'
import type { StrategicClient } from '../../strategic/client'
import { PLAYER_FACTION_ID, type CorpFactionId, type FactionId, type GalaxyCharterSummary, type GalaxyEconomyView, type RelationsView, type WantedLevel } from '../../strategic/types'
import {
  corpLawTooltip,
  corpLook,
  factionsAtWar,
  holderLook,
  loadBoardConfig,
  STATE_GLYPHS,
  STATE_LABELS,
  tariffLabel,
  tollLabel,
  type BoardConfig,
  type BoardCorp,
} from './boardModel'

export const CHARTER_OVERLAY_KINDS = ['claims', 'states', 'wars', 'law', 'tolls'] as const
export type CharterOverlayKind = (typeof CHARTER_OVERLAY_KINDS)[number]

export const CHARTER_OVERLAY_IDS: Readonly<Record<CharterOverlayKind, string>> = {
  claims: 'charter.claims',
  states: 'charter.states',
  wars: 'charter.wars',
  law: 'charter.law',
  tolls: 'charter.tolls',
}
export const CHARTER_OVERLAY_LABELS: Readonly<Record<CharterOverlayKind, string>> = {
  claims: 'Claims',
  states: 'States',
  wars: 'Wars and fronts',
  law: 'Law',
  tolls: 'Tolls',
}

const GREY = '#6B7380'
const WAR_RED = '#E0463A'
const WANTED_COLORS: readonly string[] = ['#8FA7C4', '#FFB347', '#FF7A45', '#E0463A']
const STATE_COLOR: Readonly<Record<'pending' | 'active' | 'recovering', string>> = { pending: '#FFB347', active: '#FF7A45', recovering: '#9FF5C8' }
const TOLL_COLOR = '#E8B923'

/** What the layers read. */
export interface CharterOverlayData {
  galaxy: GalaxyEconomyView | null
  relations: RelationsView | null
  /** The founded player corporation (its name and livery colour), else null or absent. */
  corp?: BoardCorp | null
}

/** A node style plus the flags the tests (and a future hatch renderer) read. */
export interface CharterNodeStyle extends MapOverlayNodeStyle {
  hatched?: boolean
  hollow?: boolean
}

function summaryOf(galaxy: GalaxyEconomyView | null, systemId: string): GalaxyCharterSummary | null {
  if (galaxy === null) return null
  for (const s of galaxy.systems) if (s.id === systemId) return s.charter ?? null
  return null
}

/** "‹Corp› claim" (charterHud.json `corpClaim.claim`). */
function corpClaimLabel(corp: BoardCorp | null | undefined): string {
  return fillLabel(CORP_CLAIM_LABELS.claim, { name: corpLook(corp).name })
}

/** Whether any system is held by the corporation. */
function corpHoldsAny(galaxy: GalaxyEconomyView | null): boolean {
  for (const s of galaxy?.systems ?? []) if (s.charter?.holder === PLAYER_FACTION_ID) return true
  return false
}

/** The player's wanted level with `faction` (0 without relations). */
export function wantedWith(relations: RelationsView | null, faction: FactionId): WantedLevel {
  return relations?.player.wanted[faction] ?? 0
}

/** The node style of `systemId` on layer `kind` (null = the map's plain style). */
export function charterNodeStyle(kind: CharterOverlayKind, data: CharterOverlayData, systemId: string, cfg: BoardConfig['overlays']): CharterNodeStyle | null {
  const c = summaryOf(data.galaxy, systemId)
  if (c === null) return null
  switch (kind) {
    case 'claims': {
      if (c.holder === null) return { color: GREY, size: cfg.hollowSize, badge: '○', hollow: true }
      const look = holderLook(c.holder, data.corp)
      return c.contested
        ? {
            color: look.color,
            size: cfg.heldSize,
            badge: `${look.mark}▨`,
            hatched: true,
          }
        : { color: look.color, size: cfg.heldSize, badge: look.mark }
    }
    case 'states': {
      if (c.states.length === 0) return null
      const worst = c.states.find((s) => s.stage === 'active') ?? c.states.find((s) => s.stage === 'pending') ?? c.states[0]
      return {
        color: STATE_COLOR[worst.stage],
        size: 1 + 0.15 * c.states.length,
        badge: c.states.map((s) => STATE_GLYPHS[s.kind]).join(''),
      }
    }
    case 'wars': {
      const warring = factionsAtWar(data.relations)
      const atWar = (c.holder !== null && c.holder !== PLAYER_FACTION_ID && warring.has(c.holder)) || c.states.some((s) => s.kind === 'war' && s.stage !== 'recovering')
      return atWar ? { color: WAR_RED, size: cfg.warSize, badge: '⚔' } : null
    }
    case 'law': {
      if (c.holder === null) return { color: GREY, size: 0.8, badge: '∅' }
      if (c.holder === PLAYER_FACTION_ID) {
        const look = corpLook(data.corp)
        return { color: look.color, size: cfg.heldSize, badge: look.mark }
      }
      const w = wantedWith(data.relations, c.holder)
      return {
        color: WANTED_COLORS[w],
        size: 1 + 0.2 * w,
        badge: w > 0 ? `W${w}` : '✓',
      }
    }
    case 'tolls': {
      if (!(c.toll > 0) && !(c.tariff > 0)) return null
      const f = Math.min(1, Math.max(0, c.toll / cfg.tollMax))
      return {
        color: TOLL_COLOR,
        size: cfg.tollMinSize + f * (cfg.tollMaxSize - cfg.tollMinSize),
        badge: c.toll > 0 ? `¤${Math.round(c.toll)}` : `%${Math.round(c.tariff * 100)}`,
      }
    }
  }
}

/** The edge style of `relayId` on layer `kind`: only the Wars layer draws the fronts. */
export function charterEdgeStyle(
  kind: CharterOverlayKind,
  data: CharterOverlayData,
  relayId: string,
  cfg: BoardConfig['overlays'],
): MapOverlayEdgeStyle | null {
  if (kind !== 'wars' || data.galaxy === null) return null
  for (const f of data.galaxy.fronts ?? []) if (f.relayId === relayId) return { color: WAR_RED, width: cfg.frontWidth, dashed: true }
  return null
}

/** Tooltip lines for `systemId` on layer `kind`. */
export function charterTooltip(kind: CharterOverlayKind, data: CharterOverlayData, systemId: string): string[] {
  const c = summaryOf(data.galaxy, systemId)
  if (c === null) return data.galaxy === null ? ['Charter data loading…'] : []
  const holder = c.holder === null ? 'Unclaimed' : c.holder === PLAYER_FACTION_ID ? corpClaimLabel(data.corp) : `${holderLook(c.holder, data.corp).name} claim`
  switch (kind) {
    case 'claims':
      return [c.contested ? `${holder} · contested` : holder, 'K: Charter Board']
    case 'states':
      return c.states.length === 0 ? ['No system states'] : c.states.map((s) => `${STATE_GLYPHS[s.kind]} ${STATE_LABELS[s.kind]} (${s.stage})`)
    case 'wars': {
      const lines: string[] = []
      for (const p of data.relations?.pairs ?? []) {
        if (p.relation !== 'war' || (p.a !== c.holder && p.b !== c.holder)) continue
        lines.push(`⚔ War: ${holderLook(p.a, data.corp).name} and ${holderLook(p.b, data.corp).name}`)
      }
      return lines.length > 0 ? lines : [holder, 'At peace']
    }
    case 'law': {
      // CL-7 (D-1158 Q3): wording only; an unclaimed system is still lawless (D-160).
      if (c.holder === null) return [UNCLAIMED_LABELS.label, UNCLAIMED_LABELS.note]
      if (c.holder === PLAYER_FACTION_ID) return [holder, corpLawTooltip()]
      const w = wantedWith(data.relations, c.holder)
      const bounty = data.relations?.player.bounties[c.holder] ?? 0
      return [`${holderLook(c.holder, data.corp).name} jurisdiction`, w > 0 ? `WANTED ${w} · bounty ${Math.round(bounty)} cr` : 'Clean']
    }
    case 'tolls':
      return [`${holder}`, `Tariff ${tariffLabel(c.tariff)} · toll ${tollLabel(c.toll)}`]
  }
}

function legendOf(kind: CharterOverlayKind, data: CharterOverlayData): { label: string; color: string }[] {
  switch (kind) {
    case 'claims': {
      const held = new Set<CorpFactionId>()
      for (const s of data.galaxy?.systems ?? []) {
        const h = s.charter?.holder ?? null
        if (h !== null) held.add(h)
      }
      return [
        ...[...held].map((f) => {
          const look = holderLook(f, data.corp)
          return { label: `${look.mark} ${look.name}`, color: look.color }
        }),
        { label: '▨ contested', color: '#E8F9FF' },
        { label: '○ unclaimed', color: GREY },
      ]
    }
    case 'states':
      return [
        { label: '■ active', color: STATE_COLOR.active },
        { label: '■ pending', color: STATE_COLOR.pending },
        { label: '■ recovering', color: STATE_COLOR.recovering },
      ]
    case 'wars':
      return [{ label: '⚔ war front (dashed)', color: WAR_RED }]
    case 'law':
      return [
        { label: '✓ clean', color: WANTED_COLORS[0] },
        { label: 'W1', color: WANTED_COLORS[1] },
        { label: 'W2', color: WANTED_COLORS[2] },
        { label: 'W3', color: WANTED_COLORS[3] },
        ...(corpHoldsAny(data.galaxy) ? [{ label: `${corpLook(data.corp).mark} ${corpClaimLabel(data.corp)}`, color: corpLook(data.corp).color }] : []),
        { label: '∅ no claim law', color: GREY },
      ]
    case 'tolls':
      return [{ label: '¤ toll (size) · % tariff', color: TOLL_COLOR }]
  }
}

/** The five layers over a data getter (the installer's live data; tests pass a fixed one). */
export function createCharterOverlayLayers(data: () => CharterOverlayData, cfg: BoardConfig['overlays'] = loadBoardConfig().overlays): MapOverlayLayer[] {
  return CHARTER_OVERLAY_KINDS.map((kind): MapOverlayLayer => ({
    id: CHARTER_OVERLAY_IDS[kind],
    label: CHARTER_OVERLAY_LABELS[kind],
    get legend(): readonly { label: string; color: string }[] {
      return legendOf(kind, data())
    },
    node: (systemId) => charterNodeStyle(kind, data(), systemId, cfg),
    edge: (relayId) => charterEdgeStyle(kind, data(), relayId, cfg),
    tooltip: (systemId) => charterTooltip(kind, data(), systemId),
    picker: null,
  }))
}

/** What the overlays follow; `corpView` (optional) names and colours the corporation's claims. */
export type CharterOverlaySource = Pick<StrategicClient, 'galaxyView' | 'onGalaxyView' | 'relations' | 'onRelations'> &
  Partial<Pick<StrategicClient, 'corpView'>>

export interface CharterOverlays {
  readonly layers: readonly MapOverlayLayer[]
  readonly subscribed: boolean
  dispose(): void
}

/** Registers the five layers; follows the galaxy and relations views while a map is open. */
export function installCharterOverlays(source: CharterOverlaySource, cfg: BoardConfig['overlays'] = loadBoardConfig().overlays): CharterOverlays {
  const data: CharterOverlayData = {
    galaxy: source.galaxyView,
    relations: source.relations,
    corp: null,
  }
  let disposed = false
  function readCorp(): void {
    const fetchCorp = source.corpView
    if (fetchCorp === undefined) return
    fetchCorp()
      .then((v) => {
        if (disposed) return
        data.corp = v.founded ? { name: v.name, color: v.livery.primary } : null
        notifyMapOverlays()
      })
      .catch((err: unknown) => {
        logger.debug('Charter overlays: no corporation view', err)
      })
  }
  let offs: (() => void)[] | null = null
  const layers = createCharterOverlayLayers(() => data, cfg)
  const unregister = layers.map((l) => registerMapOverlay(l))
  const stopOpen = onMapOpenChange((open) => {
    if (open && offs === null) {
      data.galaxy = source.galaxyView ?? data.galaxy
      data.relations = source.relations ?? data.relations
      offs = [
        source.onGalaxyView((v) => {
          data.galaxy = v
          notifyMapOverlays()
        }),
        source.onRelations((r) => {
          data.relations = r
          notifyMapOverlays()
        }),
      ]
      notifyMapOverlays()
      readCorp()
    } else if (!open && offs !== null) {
      for (const off of offs) off()
      offs = null
    }
  })
  return {
    layers,
    get subscribed(): boolean {
      return offs !== null
    },
    dispose(): void {
      disposed = true
      stopOpen()
      for (const off of offs ?? []) off()
      offs = null
      for (const u of unregister) u()
    },
  }
}
